
import Vue from 'vue';

// Services and types
import ResourceService from '~/services/ResourceService';
import { SchoolVerification, SchoolVerificationState } from '~/services/SchoolService';

// Components
import Header from '~/components/layouts/Header.vue';
import MainLayout from '~/components/layouts/mainlayout/MainLayout.vue';
import ProfileModule from '~/components/smart/ProfileModule/ProfileModule.vue';
import VerifyModule from '~/components/miscellaneous/VerifyModule.vue';
import NoContent from '~/components/miscellaneous/NoContent.vue';
import FeaturedResource from '~/components/smart/FeaturedResource.vue';
import WidgetModule from '~/components/smart/WidgetModule.vue';
import ResourcesFolderGrid from './components/ResourcesFolderGrid.vue';
import CVTemplateButton from './components/CVTemplateButton.vue';
import Spinner from '~/components/builders/Spinner.vue';
import VerifiedSchoolsSelect from '~/components/miscellaneous/VerifiedSchoolsSelect.vue';
import SchoolVerificationAlert from '~/components/miscellaneous/SchoolVerificationAlert/SchoolVerificationAlert.vue';

// Utils
import URLS from '~/URLS';
import i18n from '~/localization/i18n';
import { ResourceNode } from '~/services/types/resources';
import { RestXReactiveData } from '~/utils/types';
import { CandidateStudyProgram } from '~/services/CSPService';

// Types
type ResourceNodeWithCount = ResourceNode & { count?: number };

export default Vue.extend({
    name: 'ResourcesInternal',
    components: {
        ResourcesFolderGrid,
        Header,
        MainLayout,
        ProfileModule,
        VerifyModule,
        FeaturedResource,
        NoContent,
        CVTemplateButton,
        WidgetModule,
        Spinner,
        VerifiedSchoolsSelect,
        SchoolVerificationAlert,
    },
    data: () => ({
        search: '',
        breadcrumb: [
            {
                exact: true,
                text: i18n.t('resourcesInternal.header'),
                to: URLS.resources,
            },
        ],
        files: [] as ResourceNodeWithCount[],
        selectedSchoolId: null as string | null,
        loading: false,
    }),
    computed: {
        hasFiles(): boolean {
            return !!this.files && this.files.length > 0;
        },
        schoolVerifications(): RestXReactiveData<SchoolVerification[]> {
            return (this as any).$interfaces.university.connection.get({ reactive: true });
        },
        candidateStudyPrograms(): CandidateStudyProgram[] {
            return this.$store.getters['profileModule/getStudyPrograms']();
        },
        hasSchoolVerifications(): boolean {
            return (
                this.schoolVerifications.data.filter(
                    school => school.state === SchoolVerificationState.verified
                ).length > 0
            );
        },
    },
    methods: {
        refreshResources(): Promise<void> {
            this.files = [];

            if (!this.selectedSchoolId) {
                return Promise.resolve();
            }
            const schoolId = Number(this.selectedSchoolId);
            this.loading = true;

            return Promise.all([
                ResourceService.getRootFoldersBySchool(schoolId),
                ResourceService.getRootSystemFolders(),
            ])
                .then(([schoolResults, systemResults]) => {
                    return {
                        data: [...schoolResults.data, ...systemResults.data],
                    };
                })
                .then(async res => {
                    let files: ResourceNodeWithCount[] = res.data;
                    for (let i = 0; i < files.length; i++) {
                        const elementsData = await ResourceService.getFolderById(
                            files[i].id,
                            files[i].isSystemResource ? undefined : schoolId
                            // schoolId
                        );
                        files[i] = { ...files[i], count: elementsData.data.length };
                    }
                    this.files = files;
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        startSchoolVerification() {
            this.$router.push({
                name: 'profile',
                query: { scrollTo: 'educations' },
            });
        },
    },
    mounted() {
        this.refreshResources();
    },
    watch: {
        selectedSchoolId() {
            this.refreshResources();
        },
        hasSchoolVerifications: {
            handler(newValue: boolean) {
                if (newValue) {
                    this.selectedSchoolId = this.schoolVerifications.data
                        .filter(school => school.state === SchoolVerificationState.verified)[0]
                        .schoolId.toString();
                }
            },
            immediate: true,
        },
    },
});
